import type { ZodType } from 'zod';
import i18next from '@/lib/i18n';
import {
  createMembershipRowSchema,
  createOrgFunctionRowSchema,
  type MembershipRowInput,
  type OrgFunctionRowInput
} from '@/lib/excel';
import { zodRowValidator, type RowValidator } from '@/lib/draft-rows/draftRow';

/**
 * Wizard-context validators for the tables whose rows reference a person by
 * email (Leitung, Mitgliedschaft, System-/Prozessverantwortlicher): the email
 * must resolve to an internal member. The backend rejects unknown emails per
 * row anyway — flagging them in the table turns a submit-time banner (or a
 * server 400) into an inline cell error that blocks Weiter.
 */

const unknownMemberIssue = (email: string) => ({
  code: 'custom' as const,
  message: i18next.t('bulkOnboarding.validation.unknownMemberEmail', { email })
});

export function createMemberEmailAwareRowValidator<TData extends object>(
  createSchema: () => ZodType<TData>,
  emailFields: readonly (keyof TData & string)[],
  memberEmails: Set<string>
): RowValidator<TData> {
  return zodRowValidator(() =>
    createSchema().superRefine((row, ctx) => {
      for (const emailField of emailFields) {
        const email = row[emailField];
        if (typeof email === 'string' && email && !memberEmails.has(email.toLowerCase())) {
          ctx.addIssue({ ...unknownMemberIssue(email), path: [emailField] });
        }
      }
    })
  );
}

export function createLeadAwareOrgFunctionRowValidator(
  memberEmails: Set<string>
): RowValidator<OrgFunctionRowInput> {
  return createMemberEmailAwareRowValidator(
    createOrgFunctionRowSchema,
    ['leadEmail'],
    memberEmails
  );
}

export function createMemberAwareMembershipRowValidator(
  memberEmails: Set<string>
): RowValidator<MembershipRowInput> {
  return createMemberEmailAwareRowValidator(
    createMembershipRowSchema,
    ['memberEmail'],
    memberEmails
  );
}
